import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isNative } from '../../hooks/useNativeFeatures';
import { PATHS } from '../../routes/paths';
import Button from '../ui/Button';

const NAV_LINKS = [
{ to: PATHS.vendors, label: 'Explore' },
{ to: PATHS.events, label: 'Events' },
{ to: PATHS.map, label: 'Live Map' },
{ to: PATHS.budgetBuilder, label: 'Budget Builder', beta: true },
{ to: '/#how-it-works', label: 'How it works', anchor: true }];

/** Store link for the visitor's device; hidden inside the native app itself. */
function appStoreUrl() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/(Macintosh)/.test(ua) && navigator.maxTouchPoints > 1);
  return isIOS
    ? (process.env.REACT_APP_APPSTORE_URL || 'https://apps.apple.com/app/vgo-vendorsgoonline/id6766173854')
    : (process.env.REACT_APP_PLAYSTORE_URL || 'https://play.google.com/store/apps/details?id=com.vendorsgoonline.vgo');
}

export default function Header() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const accountButtonRef = useRef(null);
  const menuButtonRef = useRef(null);

  const dashboardPath = user?.role === 'vendor' ? PATHS.vendorDashboard : PATHS.customerDashboard;
  // A customer who claimed a vendor listing holds both roles - the menu
  // becomes the switcher between their two dashboards.
  const heldRoles = user ? (user.roles?.length ? user.roles : [user.role]) : [];
  const multiRole = heldRoles.includes('vendor') && heldRoles.includes('customer');

  // Close the account menu on outside click / Escape.
  useEffect(() => {
    if (!accountOpen) return undefined;
    const onClick = (e) => {
      if (!accountRef.current?.contains(e.target)) setAccountOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAccountOpen(false);
        accountButtonRef.current?.focus();
      }
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Back/forward and programmatic navigation should never leave an old
  // disclosure covering the newly selected page.
  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, [location.pathname, location.search]);

  const logOut = () => {
    signOut();
    setAccountOpen(false);
    setMenuOpen(false);
    navigate(PATHS.home);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:gap-7 sm:px-6">
        <Link to={PATHS.home} className="flex flex-none items-center" aria-label="VGO home">
          <img src="/logo.svg" alt="VGO" className="h-8 w-auto sm:h-9" />
        </Link>

        <nav className="hidden gap-1.5 lg:flex" aria-label="Main">
          {NAV_LINKS.map((link) =>
          link.anchor ?
          <a
            key={link.label}
            href={link.to}
            className="rounded-full px-3.5 py-2 text-[15px] font-medium text-inksoft transition-colors hover:bg-panel hover:text-ink">

                {link.label}
              </a> :

          <NavLink
            key={link.label}
            to={link.to}
            className={({ isActive }) =>
            `rounded-full px-3.5 py-2 text-[15px] font-medium transition-colors ${
            isActive ? 'bg-panel text-ink' : 'text-inksoft hover:bg-panel hover:text-ink'}`
            }>

                {link.label}
                {link.beta && <sup className="ml-1 rounded-full bg-sun px-1.5 py-0.5 text-[9px] font-extrabold text-ink">BETA</sup>}
              </NavLink>

          )}
        </nav>

        <div className="ml-auto flex items-center gap-2.5 sm:gap-3">
          {!isNative() &&
          <a
            href={appStoreUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-2 text-[13px] font-bold text-white transition-colors hover:bg-black sm:px-3.5">

              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
                <path d="M17 1H7a3 3 0 0 0-3 3v16a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V4a3 3 0 0 0-3-3Zm-5 20.5a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5ZM18 17H6V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V17Z" />
              </svg>
              Open app
            </a>
          }
          {/* Hidden once the user already has a listing (vendor role) */}
          {user?.role !== 'vendor' &&
          <Button to={PATHS.listYourService} variant="ghost" size="sm" className="hidden sm:inline-flex">
            List your service
          </Button>
          }

          {/* Account avatar + menu */}
          <div className="relative" ref={accountRef}>
            <button
              ref={accountButtonRef}
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setAccountOpen((v) => !v);
              }}
              aria-expanded={accountOpen}
              aria-controls="account-disclosure"
              aria-label="Account"
              className="grid h-11 w-11 place-items-center overflow-hidden rounded-full border border-line bg-sky text-base transition-shadow hover:shadow-card">

              {user?.avatar ?
              <img src={user.avatar} alt="" className="h-full w-full object-cover" /> :
              user ?
              <span className="font-display text-sm font-extrabold text-ink">{(user.name || user.email || '?').charAt(0).toUpperCase()}</span> :

              <svg viewBox="0 0 24 24" className="h-5 w-5 text-inksoft" fill="currentColor" aria-hidden="true">
                  <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-4.05 0-7.5 2.24-7.5 5v1.5h15V19c0-2.76-3.45-5-7.5-5Z" />
                </svg>
              }
            </button>
            {accountOpen &&
            <div
              id="account-disclosure"
              className="absolute right-0 top-[calc(100%+8px)] w-52 overflow-hidden rounded-2xl border border-line bg-white py-1.5 shadow-lift">

                {user ?
              <>
                    <p className="border-b border-line px-4 py-2.5 text-[13px] text-inksoft">
                      Signed in as <span className="block truncate font-semibold text-ink">{user.name}</span>
                    </p>
                    {multiRole ? <>
                      <MenuLink to={PATHS.vendorDashboard} onClick={() => setAccountOpen(false)}>Vendor dashboard</MenuLink>
                      <MenuLink to={PATHS.customerDashboard} onClick={() => setAccountOpen(false)}>Customer dashboard</MenuLink>
                    </> :
                    <MenuLink to={dashboardPath} onClick={() => setAccountOpen(false)}>Dashboard</MenuLink>
                    }
                    <MenuLink to={PATHS.messages} onClick={() => setAccountOpen(false)}>Messages</MenuLink>
                    <MenuLink to={user.role === 'vendor' ? PATHS.vendorSettings : PATHS.customerSettings} onClick={() => setAccountOpen(false)}>Settings</MenuLink>
                    {user.role !== 'vendor' &&
                <MenuLink to={PATHS.listYourService} onClick={() => setAccountOpen(false)} className="font-semibold text-brand">
                      List your service
                    </MenuLink>
                }
                    <button
                  type="button"
                  onClick={logOut}
                  className="block w-full px-4 py-2.5 text-left text-sm font-medium text-inksoft hover:bg-panel hover:text-ink">

                      Log out
                    </button>
                  </> :

              <>
                    <MenuLink to={PATHS.login} onClick={() => setAccountOpen(false)}>Log in</MenuLink>
                    <MenuLink to={PATHS.signup} onClick={() => setAccountOpen(false)} className="font-semibold text-brand">Sign up</MenuLink>
                  </>
              }
              </div>
            }
          </div>

          <button
            ref={menuButtonRef}
            type="button"
            className="grid h-11 w-11 place-items-center rounded-full border border-line lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            aria-label="Toggle menu"
            onClick={() => {
              setAccountOpen(false);
              setMenuOpen((v) => !v);
            }}>

            <svg viewBox="0 0 24 24" className="h-5 w-5 stroke-ink" fill="none" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {menuOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {menuOpen &&
      <nav
        id="mobile-navigation"
        className="overflow-y-auto overscroll-contain border-t border-line bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden"
        style={{ maxHeight: 'calc(100dvh - 4rem - env(safe-area-inset-top))' }}
        aria-label="Mobile">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) =>
          link.anchor ?
          <a key={link.label} href={link.to} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-panel">
                  {link.label}
                </a> :

          <Link key={link.label} to={link.to} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-panel">
                  {link.label}
                  {link.beta && <sup className="ml-1 rounded-full bg-sun px-1.5 py-0.5 text-[9px] font-extrabold text-ink">BETA</sup>}
                </Link>

          )}
            {user?.role !== 'vendor' &&
          <Link to={PATHS.listYourService} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-semibold text-brand hover:bg-brand-tint">
              List your service
            </Link>
          }
            {user ?
          <>
                {multiRole ? <>
                  <Link to={PATHS.vendorDashboard} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-panel">
                    Vendor dashboard
                  </Link>
                  <Link to={PATHS.customerDashboard} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-panel">
                    Customer dashboard
                  </Link>
                </> :
                <Link to={dashboardPath} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-panel">
                  Dashboard
                </Link>
                }
                <Link to={PATHS.messages} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-panel">
                  Messages
                </Link>
                <button
              onClick={logOut}
              className="rounded-xl px-3 py-2.5 text-left text-sm font-medium text-inksoft hover:bg-panel">

                  Log out
                </button>
              </> :

          <>
                <Link to={PATHS.login} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-semibold hover:bg-panel">
                  Log in
                </Link>
                <Link to={PATHS.signup} onClick={() => setMenuOpen(false)} className="rounded-xl px-3 py-2.5 text-sm font-semibold text-brand hover:bg-brand-tint">
                  Sign up
                </Link>
              </>
          }
          </div>
        </nav>
      }
    </header>);

}

function MenuLink({ to, onClick, className = '', children }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-4 py-2.5 text-sm font-medium text-inksoft hover:bg-panel hover:text-ink ${className}`}>

      {children}
    </Link>);

}
